import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import Panel from 'utils/panel';
import { formatMoney, formatCartLinesAjax } from 'utils/storefront/format-resource';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import CartUpsell from './cart-upsell';
import CartEmpty from './cart-empty';
import CloseIcon from '../utils/icon/icon-close.svg';
import CartIncentive from './cart-incentive';
import DiscountTag from '../discount-tag';

const Cart = ({ cart, data: { upsell, emptyCart, cartIncentive, discountTag } }) => {
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get('cart');
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  const lineItems = cart?.mode === 'storefront' ? cart?.state?.lines : formatCartLinesAjax(cart?.state?.items);

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      context="cart"
      overlay={true}
      onOpen={() => document.dispatchEvent(new CustomEvent('cart:opened'))}
    >
      <div className="mini-cart flex h-full flex-col">
        <div className="flex items-center justify-between px-8 pb-3 pt-5">
          <div className="heading-4">
            {window.TMW.Strings.cart.miniCartTitle} ({cart?.state?.item_count})
          </div>
          <button
            type="button"
            ref={closeTriggerEl}
            aria-label={window.TMW.Strings.cart.close}
            className="[&>svg]:size-6"
          >
            <CloseIcon />
          </button>
        </div>
        <div
          className={clsx('flex flex-1 flex-col overflow-y-auto scroll-smooth')}
          data-cart-data
        >
          <CartIncentive
            cart={cart}
            cta={cartIncentive.link}
          />

          {lineItems.length > 0 && discountTag.message && discountTag.showDiscountTag && (
            <div className={`pb-0 pl-3 pr-3 pt-3 md:pl-8 md:pr-8`}>
              <div className={`${discountTag.colorScheme}`}>
                <DiscountTag
                  className={`w-full text-center color-${discountTag.colorScheme}`}
                  message={discountTag.message}
                />
              </div>
            </div>
          )}
          <div
            className={clsx('mb-3 flex flex-col gap-y-8 p-8', {
              hidden: cart.state.item_count < 1,
            })}
          >
            {lineItems.map((lineItem) => (
              <CartItem
                key={lineItem.id}
                lineItem={lineItem}
              />
            ))}
          </div>
          {cart.state.item_count === 0 && <CartEmpty {...emptyCart} />}
          <CartUpsell
            upsellData={upsell}
            cartEmpty={lineItems.length === 0}
            cartItems={lineItems}
          />
        </div>
        {lineItems?.length > 0 && (
          <div className="flex flex-col content-stretch gap-2 border-t border-t-brand-tangerine px-3 py-5 md:px-8">
            {cart?.state?.total_discount > 0 && (
              <div className="flex justify-between gap-1">
                <div className="body-3-bold flex-none text-error">{window.TMW.Strings.cart.total_savings}</div>

                <div className="body-3-bold flex-none text-error">
                  {formatMoney(
                    cart?.state?.total_discount ? { amount: cart.state.total_discount } : { amount: 0 },
                    true,
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-1">
              <div className="body-3-bold flex-none">{window.TMW.Strings.cart.subtotal}</div>
              <div
                className="body-3 flex-auto"
                dangerouslySetInnerHTML={{ __html: window.TMW.Strings.cart.shipping_calculated_at_checkout }}
              ></div>
              <div className="body-3-bold flex-none">
                {cart.mode === 'storefront'
                  ? formatMoney(cart?.state?.subtotal || { amount: 0 })
                  : formatMoney(cart?.state?.total_price ? { amount: cart.state.total_price } : { amount: 0 }, true)}
              </div>
            </div>
            <a
              className="button button--primary w-full justify-center"
              id="cart-checkout-button"
              href="/checkout"
            >
              {cart.mode === 'storefront' ? cart?.state?.checkoutUrl : window.TMW.Strings.cart.checkout}
            </a>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);
